// GlossaryContext to optimize glossary data management and reduce memory usage
import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

const GlossaryContext = createContext(null);

// Lazy load glossary data
const loadGlossaryData = async () => {
  try {
    const response = await fetch('https://btclexicon.com/api/v2/terms');
    return await response.json();
  } catch (error) {
    console.error('Failed to load glossary data:', error);
    return [];
  }
};

export const GlossaryProvider = ({ children }) => {
  const [glossaryData, setGlossaryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [searchCache, setSearchCache] = useState(new Map());
  
  // Lazy load glossary data only when needed
  const loadGlossary = useCallback(async () => {
    if (isLoaded || isLoading) return;
    
    setIsLoading(true);
    const data = await loadGlossaryData();
    setGlossaryData(data);
    setIsLoaded(true);
    setIsLoading(false);
  }, [isLoaded, isLoading]);
  
  // Search with caching
  const searchGlossary = useCallback((searchTerm) => {
    if (!searchTerm) return glossaryData;
    
    // Check cache first
    const cacheKey = searchTerm.toLowerCase();
    if (searchCache.has(cacheKey)) {
      return searchCache.get(cacheKey);
    }
    
    // Perform search
    const results = glossaryData.filter(item => 
      item.title.toLowerCase().includes(cacheKey) ||
      item.definition.toLowerCase().includes(cacheKey)
    );
    
    // Cache results (limit cache size)
    if (searchCache.size > 50) {
      const firstKey = searchCache.keys().next().value;
      searchCache.delete(firstKey);
    }
    searchCache.set(cacheKey, results);
    
    return results;
  }, [glossaryData, searchCache]);
  
  // Get term by ID or title
  const getTerm = useCallback((termId) => {
    return glossaryData.find(item => 
      item.id === termId || item.title.toLowerCase() === termId.toLowerCase()
    );
  }, [glossaryData]);
  
  // Memoized value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    glossaryData,
    isLoading,
    isLoaded,
    loadGlossary,
    searchGlossary,
    getTerm
  }), [glossaryData, isLoading, isLoaded, loadGlossary, searchGlossary, getTerm]);
  
  // Clear cache periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setSearchCache(new Map());
    }, 300000); // Clear every 5 minutes
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <GlossaryContext.Provider value={value}>
      {children}
    </GlossaryContext.Provider>
  );
};

// Custom hook to use glossary context
export const useGlossary = () => {
  const context = useContext(GlossaryContext);
  if (!context) {
    throw new Error('useGlossary must be used within a GlossaryProvider');
  }
  return context;
};

// Hook for components that need glossary data
export const useGlossaryData = () => {
  const { glossaryData, isLoading, isLoaded, loadGlossary } = useGlossary();
  
  useEffect(() => {
    if (!isLoaded && !isLoading) {
      loadGlossary();
    }
  }, [isLoaded, isLoading, loadGlossary]);
  
  return { glossaryData, isLoading };
};